import type { LatestPositionsResponse, PositionFix } from '@core/types/api';

/**
 * Folds a batch of incoming fixes into the cached snapshot.
 *
 * Extracted from the provider on purpose: this is the subtlest logic in the client and it
 * has nothing to do with React, EventSource or timers. Testing it directly means the
 * awkward cases -- replay after reconnect, a duplicate, a tracker nobody has heard of --
 * are cheap to cover.
 */
export function mergePositions(
  previous: LatestPositionsResponse | undefined,
  batch: readonly PositionFix[],
  serverTime?: string,
): LatestPositionsResponse | undefined {
  if (batch.length === 0) return previous;

  const base: LatestPositionsResponse = previous ?? { serverTime: serverTime ?? '', positions: {} };
  const next: Record<string, PositionFix> = { ...base.positions };
  let changed = false;

  for (const fix of batch) {
    const current = next[fix.trackerId];

    // Out-of-order guard. Replay after a reconnect deliberately overlaps, and a device
    // returning from a dead zone flushes its buffer in arbitrary order -- so an older fix
    // arriving after a newer one is routine, not exceptional. Without this the marker
    // jumps backwards and then forwards again, which reads as the map being broken.
    if (current && !isNewer(fix, current)) {
      continue;
    }

    next[fix.trackerId] = fix;
    changed = true;
  }

  // Identity-stable when nothing actually changed. A fresh object here would re-render
  // every consumer of the cache -- including the map -- on every duplicate frame.
  if (!changed) return previous;

  return { serverTime: serverTime ?? base.serverTime, positions: next };
}

/**
 * Whether `fix` should replace `current` as a tracker's head.
 *
 * <p>Ordered by the DEVICE clock, exactly like the server's own head rule. Arrival order
 * is not position order: a buffered upload delivers several fixes within a second, the
 * server ingests them on parallel threads, and a replay re-sends fixes the server never
 * made the head. Ordering by `receivedAt` let such a fix -- recorded earlier, ingested a
 * moment later -- step the pin backwards after every reconnect. Arrival time and id only
 * break exact ties.
 */
function isNewer(fix: PositionFix, current: PositionFix): boolean {
  const recorded = Date.parse(fix.recordedAt) - Date.parse(current.recordedAt);
  if (recorded !== 0) return recorded > 0;
  const received = Date.parse(fix.receivedAt) - Date.parse(current.receivedAt);
  if (received !== 0) return received > 0;
  return fix.id > current.id;
}
